import { useCallback, useEffect, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent, RefObject } from "react";
import { addDays, weekday } from "../tickets/timelineModel.js";
import { shiftAnchor } from "./calendarModel.js";
import type { CalendarView } from "./calendarModel.js";

/**
 * TASTATUR IM RASTER DES KALENDERS (Spec D23 „Tastatur“) — wandernder Tabstopp über die Tage (`role="gridcell"` mit
 * `data-day`): Pfeile ± 1 bzw. ± 7 Tage, Pos1/Ende Wochenanfang/-ende, Bild auf/ab vorige/nächste Periode, `0` heute.
 * Liegt der neue Tag außerhalb des Fensters, meldet `onLeave` ihn (der Bereich ersetzt die Adresse) — der Fokus folgt,
 * sobald die Zelle gezeichnet ist. Enter (bzw. Leertaste) geht in die Einträge des Tages (`data-entry`; sie sind dann per
 * Tab erreichbar), ohne Einträge meldet `onActivate` den Tag (Jahr: Sicht Tag, Telefon: Tagesliste). Escape aus einem
 * Eintrag führt zurück zum Tag. Der Tabstopp beginnt am Anker und springt mit ihm (Blättern, Adresse).
 */

export type CalendarKeysOptions = {
  view: CalendarView; anchor: string; heute: string; from: string; to: string;
  /** Der Behälter der Tage (Raster der Woche bzw. des Monats, alle Monate des Jahres). */
  gridRef: RefObject<HTMLElement | null>;
  onLeave: (day: string) => void;
  onActivate: (day: string) => void;
};

export type CalendarKeys = {
  /** Der Tag mit dem Tabstopp (im Fenster). */
  focusDay: string;
  /** Der Tag, dessen Einträge nach Enter oder einem Klick in den Tag per Tab erreichbar sind; sonst `null`. */
  insideDay: string | null;
  /** Klick oder Antippen eines Tages: der Tabstopp wandert dorthin, seine Einträge sind offen (Telefon: die Tagesliste
   *  darunter). */
  choose: (day: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onBlur: (e: FocusEvent<HTMLElement>) => void;
};

/** Der neue Tag je Taste; `null` = keine Taste des Rasters. */
function target(key: string, day: string, view: CalendarView, heute: string): string | null {
  switch (key) {
    case "ArrowLeft": return addDays(day, -1);
    case "ArrowRight": return addDays(day, 1);
    case "ArrowUp": return addDays(day, -7);
    case "ArrowDown": return addDays(day, 7);
    case "Home": return addDays(day, -weekday(day));
    case "End": return addDays(day, 6 - weekday(day));
    case "PageUp": return shiftAnchor(view, day, -1);
    case "PageDown": return shiftAnchor(view, day, 1);
    case "0": return heute;
    default: return null;
  }
}

export function useCalendarKeys(o: CalendarKeysOptions): CalendarKeys {
  const [focusDay, setFocusDay] = useState(o.anchor);
  const [insideDay, setInsideDay] = useState<string | null>(null);
  // Muster „Zustand bei geänderter Eingabe zurücksetzen“: ein neuer Anker (Blättern, Heute, Adresse) trägt den Tabstopp.
  const [anchorSeen, setAnchorSeen] = useState(o.anchor);
  if (anchorSeen !== o.anchor) {
    setAnchorSeen(o.anchor);
    setFocusDay(o.anchor);
    setInsideDay(null);
  }
  const inWindow = focusDay >= o.from && focusDay <= o.to;
  // Den Fokus nur nach einer Taste setzen — und erst, wenn die Zelle gezeichnet ist (nach `onLeave` im neuen Fenster);
  // hat der Benutzer den Fokus inzwischen woanders hingesetzt, bleibt er dort. Die Ref trägt den Tag selbst: Ein noch
  // ausstehender Effekt eines älteren Zeichnens (React holt ihn vor dem nächsten Ereignis nach) sucht so denselben Tag.
  const wantFocus = useRef<string | null>(null);
  useEffect(() => {
    const grid = o.gridRef.current;
    if (wantFocus.current === null || grid === null) return;
    const cell = grid.querySelector<HTMLElement>(`[data-day="${wantFocus.current}"]`);
    if (cell === null) return;
    wantFocus.current = null;
    const active = document.activeElement;
    if (active === null || active === document.body || grid.contains(active)) cell.focus();
  });

  const { view, heute, from, to, onLeave, onActivate } = o;
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey || e.defaultPrevented) return;
    const el = e.target as HTMLElement;
    const cell = el.closest<HTMLElement>("[data-day]");
    const day = cell?.dataset.day;
    if (cell === null || day === undefined) return;
    if (el !== cell) {
      // In einem Eintrag: Escape führt zurück zum Tag; Tab wandert von selbst durch die Einträge.
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      setInsideDay(null);
      cell.focus();
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const first = cell.querySelector<HTMLElement>("[data-entry]");
      if (first === null) { onActivate(day); return; }
      setInsideDay(day);
      first.focus();
      return;
    }
    const next = target(e.key, day, view, heute);
    if (next === null) return;
    e.preventDefault();
    setInsideDay(null);
    setFocusDay(next);
    wantFocus.current = next;
    if (next < from || next > to) onLeave(next);
  }, [view, heute, from, to, onLeave, onActivate]);

  // Verlässt der Fokus den Tag, dessen Einträge offen waren, gilt wieder nur der Tabstopp am Tag.
  const onBlur = useCallback((e: FocusEvent<HTMLElement>) => {
    if (insideDay === null) return;
    const next = e.relatedTarget;
    const cell = next instanceof Element ? next.closest<HTMLElement>("[data-day]") : null;
    if (cell?.dataset.day !== insideDay) setInsideDay(null);
  }, [insideDay]);

  // M1: Ein Klick in die Spalte fokussiert sie — wie Enter öffnet er ihre Einträge (in der Woche mit „Ticket anlegen“);
  // der Tabstopp je Tag bleibt, weil nur die gewählte Spalte ihre Einträge und Knöpfe in die Tab-Folge gibt.
  const choose = useCallback((day: string) => { setFocusDay(day); setInsideDay(day); }, []);
  return { focusDay: inWindow ? focusDay : o.anchor, insideDay, choose, onKeyDown, onBlur };
}
